import { useMemo } from 'react';

import type { PageLayoutTab } from '@backstage/frontend-plugin-api';
import {
  ENTITY_PROVIDERS_SETTINGS_PATH,
  useEntityProvidersAdmin,
} from '@internal/backstage-plugin-entity-providers-frontend';

/**
 * Page tabs that only some users may see. A tab's id is the path its sub-page
 * extension is mounted at.
 */
const RESTRICTED_TAB_IDS = [ENTITY_PROVIDERS_SETTINGS_PATH];

/**
 * Drops the tabs the signed-in user is not allowed to see.
 *
 * Hiding a tab is cosmetic - each restricted page re-checks access against its
 * own backend, which is where the gate actually lives. The membership lookup is
 * skipped on pages without a restricted tab, which is all of them but settings.
 */
export const useVisibleTabs = <T extends PageLayoutTab>(
  tabs?: T[],
): T[] | undefined => {
  const hasRestrictedTab = !!tabs?.some(tab =>
    RESTRICTED_TAB_IDS.includes(tab.id),
  );
  const isEntityProvidersAdmin = useEntityProvidersAdmin(hasRestrictedTab);

  return useMemo(() => {
    if (!hasRestrictedTab) {
      return tabs;
    }
    return tabs?.filter(tab =>
      tab.id === ENTITY_PROVIDERS_SETTINGS_PATH ? isEntityProvidersAdmin : true,
    );
  }, [tabs, hasRestrictedTab, isEntityProvidersAdmin]);
};
